import { useCallback } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useSavedViewTracking } from '~publish/hooks/useSavedViewTracking'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { writeSavedViewsList } from './savedViewsCache'
import type { SavedView } from './useSavedViewsQuery'
import { missingOrganizationOutcome } from './utils'
import { DeleteSavedViewMutation } from '../graphql/DeleteSavedViewMutation'
import { SavedViewsQuery } from '../graphql/SavedViewsQuery'

// Callers pass the full identity (not just an id) so the Saved View Deleted
// event can include the section/view — the server response only echoes the id.
type DeleteSavedViewArgs = Pick<SavedView, 'id' | 'section' | 'view'>

type DeleteSavedViewMutationResult = ReturnType<
  typeof useTypedMutation<
    'DeleteSavedViewSuccess',
    ResultOf<typeof DeleteSavedViewMutation>['deleteSavedView'],
    ResultOf<typeof DeleteSavedViewMutation>,
    VariablesOf<typeof DeleteSavedViewMutation>
  >
>

type UseDeleteSavedViewMutationResult = readonly [
  (
    view: DeleteSavedViewArgs,
  ) => Promise<Awaited<ReturnType<DeleteSavedViewMutationResult[0]>>>,
  DeleteSavedViewMutationResult[1],
]

/**
 * Delete a saved view with optimistic cache eviction and success/error toasts.
 */
export function useDeleteSavedViewMutation(): UseDeleteSavedViewMutationResult {
  const organizationId = useCurrentOrganization()?.id
  const { trackDeleted } = useSavedViewTracking()

  const [mutate, result] = useTypedMutation(
    DeleteSavedViewMutation,
    (data) => data.deleteSavedView,
    {
      successTypename: 'DeleteSavedViewSuccess',
      refetchQueries: [SavedViewsQuery],
      awaitRefetchQueries: true,
    },
  )

  const deleteSavedView = useCallback(
    async (view: DeleteSavedViewArgs) => {
      if (!organizationId) return missingOrganizationOutcome()

      const { id } = view
      const outcome = await mutate({
        variables: {
          input: { id, organizationId },
        },
        optimisticResponse: {
          deleteSavedView: {
            __typename: 'DeleteSavedViewSuccess',
            id,
          },
        },
        update: (cache, { data }) => {
          if (!isOfType(data?.deleteSavedView, 'DeleteSavedViewSuccess')) return

          writeSavedViewsList(cache, organizationId, (current) =>
            current.filter((v) => v.id !== id),
          )

          cache.evict({ id: cache.identify({ __typename: 'SavedView', id }) })
          cache.gc()
        },
      })

      if (outcome.success) {
        toast.success('View deleted')
        trackDeleted({ id, section: view.section, view: view.view })
      } else {
        toast.error(outcome.error.message)
      }

      return outcome
    },
    [mutate, organizationId, trackDeleted],
  )

  return [deleteSavedView, result] as const
}
